import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card'
import { formatPercent } from './formatters'
import type { CsatPanel } from '@/types/analytics'

interface CsatTrendCardProps {
  data: CsatPanel
}

export function CsatTrendCard({ data }: CsatTrendCardProps) {
  return (
    <Card className="p-4">
      <CardHeader className="px-0 pb-3 flex-row items-start justify-between space-y-0">
        <div>
          <CardTitle className="text-base">CSAT trend</CardTitle>
          <CardDescription>Average rating over time</CardDescription>
        </div>
        <div className="text-right">
          <div className="text-2xl font-semibold tabular-nums">
            {data.average != null ? `${data.average.toFixed(1)} ★` : '—'}
          </div>
          <div className="text-xs text-muted-foreground">
            {data.response_count} response{data.response_count === 1 ? '' : 's'}
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-0">
        {data.trend.length === 0 ? (
          <div className="h-32 mt-2 rounded-md bg-gradient-to-b from-muted/30 to-transparent flex items-center justify-center">
            <span className="text-xs text-muted-foreground">No ratings in this range yet.</span>
          </div>
        ) : (
          <div className="h-32 mt-2">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.trend} margin={{ top: 4, right: 4, bottom: 0, left: -24 }}>
                <defs>
                  <linearGradient id="csatFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--color-warning)" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="var(--color-warning)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" hide />
                <YAxis domain={[1, 5]} ticks={[1, 3, 5]} tick={{ fontSize: 10 }} width={32} />
                <Tooltip
                  formatter={(value) => [`${Number(value).toFixed(1)} ★`, 'Avg rating']}
                  labelClassName="text-xs"
                  contentStyle={{ fontSize: 12 }}
                />
                <Area
                  type="monotone"
                  dataKey="average"
                  stroke="var(--color-warning)"
                  strokeWidth={2}
                  fill="url(#csatFill)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
        <div className="mt-3 flex gap-6 text-xs text-muted-foreground">
          <span>
            Positive (4–5★):{' '}
            <span className="font-medium text-foreground">{formatPercent(data.positive_rate)}</span>
          </span>
          <span>
            Response rate:{' '}
            <span className="font-medium text-foreground">{formatPercent(data.response_rate)}</span>
          </span>
        </div>
      </CardContent>
    </Card>
  )
}
